/**
 * PKCE (RFC 7636) and the callback parsing that goes with it.
 *
 * Hand-rolled rather than pulled from an SDK: the whole flow is the two
 * functions below plus a form POST, and a dependency here would be a
 * dependency in the bundle every legal staffer downloads.
 *
 * Everything takes its randomness and hashing through an injected `Crypto`,
 * so the tests run in the `node` environment `vite.config.ts` sets without a
 * DOM.
 */

/**
 * Length of the code verifier in bytes. RFC 7636 §4.1 allows 43–128
 * characters after encoding; 32 bytes lands at 43 and is the length the
 * specification's own example uses.
 */
const VERIFIER_BYTES = 32;

/** Bytes of randomness behind the `state` parameter. */
const STATE_BYTES = 16;

function toBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function randomBase64Url(crypto: Crypto, byteLength: number): string {
  return toBase64Url(crypto.getRandomValues(new Uint8Array(byteLength)));
}

export function createCodeVerifier(crypto: Crypto): string {
  return randomBase64Url(crypto, VERIFIER_BYTES);
}

export function createState(crypto: Crypto): string {
  return randomBase64Url(crypto, STATE_BYTES);
}

/**
 * The S256 challenge. Cognito accepts `plain` too, but `plain` gives away the
 * verifier to anything that can read the authorization URL — which is the
 * attack PKCE exists to stop.
 */
export async function createCodeChallenge(
  crypto: Crypto,
  verifier: string,
): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier),
  );
  return toBase64Url(new Uint8Array(digest));
}

export interface AuthorizeUrlParams {
  readonly domain: string;
  readonly clientId: string;
  readonly redirectUri: string;
  readonly scopes: readonly string[];
  readonly state: string;
  readonly codeChallenge: string;
  /**
   * Which identity provider to go straight to. The pool lists Google as its
   * only provider, so without this Cognito shows its own page whose single
   * button is "continue with Google" — one click that says nothing.
   */
  readonly identityProvider?: string;
}

export function buildAuthorizeUrl(params: AuthorizeUrlParams): string {
  const query = new URLSearchParams({
    // Code only. The implicit flow would put the token in a URL fragment,
    // where it survives in history and in any referrer.
    response_type: "code",
    client_id: params.clientId,
    redirect_uri: params.redirectUri,
    scope: params.scopes.join(" "),
    state: params.state,
    code_challenge: params.codeChallenge,
    code_challenge_method: "S256",
    ...(params.identityProvider === undefined
      ? {}
      : { identity_provider: params.identityProvider }),
  });
  return `${trimTrailingSlash(params.domain)}/oauth2/authorize?${query}`;
}

export function buildLogoutUrl(params: {
  domain: string;
  clientId: string;
  logoutUri: string;
}): string {
  const query = new URLSearchParams({
    client_id: params.clientId,
    logout_uri: params.logoutUri,
  });
  return `${trimTrailingSlash(params.domain)}/logout?${query}`;
}

export function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, "");
}

export type CallbackResult =
  | { readonly kind: "code"; readonly code: string; readonly state: string }
  | { readonly kind: "error"; readonly message: string };

/**
 * Read the authorization response out of the callback URL's query string.
 *
 * Cognito reports a refused sign-in — an address outside the allowed domain,
 * say — as `?error=...` on the same redirect, so the error branch is a normal
 * outcome rather than an edge case.
 */
export function parseCallbackQuery(search: string): CallbackResult | null {
  const params = new URLSearchParams(search);

  const error = params.get("error");
  if (error !== null) {
    const description = params.get("error_description");
    return {
      kind: "error",
      message: description === null ? error : `${error}: ${description}`,
    };
  }

  const code = params.get("code");
  const state = params.get("state");
  if (code === null || state === null) {
    // Not a callback at all — someone opened /auth/callback directly.
    return null;
  }
  return { kind: "code", code, state };
}
